import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, User, ChevronDown, Building2, LayoutDashboard, UserCircle, LogOut, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { fetchUserCompany } from "@/services/diagnosticService";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ab05c7aa-1b14-4fc7-8b6f-1f6c043f72da/artifacts/9wfik0s6_logo%20complet%20sombre1.png";

const navLinks = [
  { label: "Accueil", path: "/" },
  { label: "Services", path: "/services" },
  { label: "Diagnostic", path: "/diagnostic" },
  { label: "Processus", path: "/processus" },
  { label: "A propos", path: "/a-propos" },
  { label: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [companyName, setCompanyName] = useState(null);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  // Fetch company name when user is logged in
  useEffect(() => {
    if (user) {
      fetchUserCompany(user.id)
        .then((company) => {
          if (company?.company_name) {
            setCompanyName(company.company_name);
          }
        })
        .catch(() => {
          // Silently fail - company may not exist yet
        });
    } else {
      setCompanyName(null);
    }
  }, [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut();
      setDropdownOpen(false);
      navigate("/");
    } catch (error) {
      console.error("Erreur lors de la déconnexion:", error);
    }
  };

  return (
    <nav data-testid="navbar" className="fixed top-0 left-0 right-0 z-50 bg-[#0B1F3B]/90 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" data-testid="navbar-logo" className="flex items-center shrink-0">
            <img src={LOGO_URL} alt="Finovéo Conseil" className="h-10 w-auto" />
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace("/", "") || "home"}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  location.pathname === link.path
                    ? "text-[#59C3A6] bg-[#59C3A6]/10"
                    : "text-slate-300 hover:text-white hover:bg-white/5"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  data-testid="user-dropdown-trigger"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 text-slate-300 hover:text-white hover:bg-white/5 px-3 py-2 rounded-full transition-all duration-200"
                >
                  <User size={18} />
                  <ChevronDown size={14} className={`transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {dropdownOpen && (
                  <div 
                    data-testid="user-dropdown-menu"
                    className="absolute right-0 mt-2 w-64 bg-[#0F2847] border border-white/10 rounded-xl shadow-2xl py-2 z-50"
                  >
                    {/* User Info Header */}
                    <div className="px-4 py-3 border-b border-white/10">
                      <p data-testid="dropdown-email" className="text-sm text-white font-medium truncate">
                        {user.email}
                      </p>
                      {companyName && (
                        <p data-testid="dropdown-company" className="text-xs text-slate-400 mt-1 flex items-center gap-1.5">
                          <Building2 size={12} />
                          {companyName}
                        </p>
                      )}
                    </div>

                    {/* Menu Items */}
                    <div className="py-1">
                      <Link
                        to="/mon-espace"
                        data-testid="dropdown-mon-espace"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
                      >
                        <LayoutDashboard size={16} />
                        Mon espace
                      </Link>
                      <Link
                        to="/mon-profil"
                        data-testid="dropdown-mon-profil"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
                      >
                        <UserCircle size={16} />
                        Mon profil
                      </Link>
                    </div>

                    {/* Logout */}
                    <div className="border-t border-white/10 pt-1">
                      <button
                        data-testid="dropdown-logout"
                        onClick={handleLogout}
                        className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                      >
                        <LogOut size={16} />
                        Se déconnecter
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/connexion">
                <Button
                  data-testid="navbar-login"
                  variant="ghost"
                  className="text-slate-300 hover:text-white hover:bg-white/5 font-medium px-4 py-2 rounded-full transition-all"
                >
                  <LogIn size={16} className="mr-2" />
                  Se connecter
                </Button>
              </Link>
            )}
            <Link to="/diagnostic">
              <Button
                data-testid="navbar-cta"
                className="bg-[#59C3A6] text-[#0B1F3B] hover:bg-[#4AB095] font-semibold px-6 py-2.5 rounded-full transition-all duration-300 hover:scale-105 shadow-[0_0_20px_rgba(89,195,166,0.25)]"
              >
                Diagnostic gratuit
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 text-slate-300 hover:text-white"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#0B1F3B]/98 backdrop-blur-xl border-t border-white/5">
          <div className="px-6 py-6 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileOpen(false)}
                className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                  location.pathname === link.path
                    ? "text-[#59C3A6] bg-[#59C3A6]/10"
                    : "text-slate-300 hover:text-white hover:bg-white/5"
                }`}
              >
                {link.label}
              </Link>
            ))}
            
            {user ? (
              <>
                {/* User info in mobile */}
                <div className="px-4 py-3 border-t border-white/10 mt-2">
                  <p className="text-sm text-white font-medium truncate">{user.email}</p>
                  {companyName && (
                    <p className="text-xs text-slate-400 mt-1 flex items-center gap-1.5">
                      <Building2 size={12} />
                      {companyName}
                    </p>
                  )}
                </div>
                <Link 
                  to="/mon-espace" 
                  onClick={() => setMobileOpen(false)} 
                  className="flex items-center gap-3 px-4 py-3 text-slate-300 hover:text-white hover:bg-white/5 rounded-lg"
                >
                  <LayoutDashboard size={18} />
                  Mon espace
                </Link>
                <Link 
                  to="/mon-profil" 
                  onClick={() => setMobileOpen(false)} 
                  className="flex items-center gap-3 px-4 py-3 text-slate-300 hover:text-white hover:bg-white/5 rounded-lg"
                >
                  <UserCircle size={18} />
                  Mon profil
                </Link>
                <button
                  onClick={() => {
                    handleLogout();
                    setMobileOpen(false);
                  }}
                  className="flex items-center gap-3 w-full px-4 py-3 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg"
                >
                  <LogOut size={18} />
                  Se déconnecter
                </button>
              </>
            ) : (
              <Link 
                to="/connexion" 
                onClick={() => setMobileOpen(false)} 
                className="flex items-center gap-3 px-4 py-3 text-slate-300 hover:text-white hover:bg-white/5 rounded-lg border-t border-white/10 mt-2"
              >
                <LogIn size={18} />
                Se connecter
              </Link>
            )}
            
            <Link to="/diagnostic" onClick={() => setMobileOpen(false)} className="block pt-2">
              <Button className="w-full bg-[#59C3A6] text-[#0B1F3B] hover:bg-[#4AB095] font-semibold rounded-full">
                Diagnostic gratuit
              </Button>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
